import { useRef, useState } from "react";
import { api } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { FileText, Upload, Download, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { safeDownloadBlob } from "../../lib/downloadUtils";

const API = process.env.REACT_APP_BACKEND_URL;

/** P0-3: sección "Documentos" de la ficha de Iniciativa — los que vienen de
 * Airtable (adjuntos migrados a Object Storage) se ven igual que los que se
 * suban aquí a mano. */
export function InitiativeDocumentsCard({ initiative, token, onChange }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const docs = initiative.documents || [];

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.uploadInitiativeDocument(token, initiative.id, file);
      onChange(res.documents);
      toast.success("Documento subido");
    } catch {
      toast.error("Error al subir el documento");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleDelete = async (idx) => {
    try {
      const res = await api.deleteInitiativeDocument(token, initiative.id, idx);
      onChange(res.documents);
      toast.success("Documento eliminado");
    } catch {
      toast.error("Error al eliminar el documento");
    }
  };

  // P0-3 fix (testing agent iter115): el enlace de descarga necesita el Bearer
  // token — un <a href> plano no lo manda y devuelve 403. Reusamos el mismo
  // fetch-como-blob que ya usa InitiativeTrackingTab para los documentos de solo lectura.
  // Fix 2026-10-25 (ticket R-4): pide primero una URL firmada de corta duración
  // (mismo patrón que la portada y los documentos PG07/ISO).
  const handleDownload = async (doc, idx) => {
    try {
      const urlRes = await fetch(`${API}/api/initiatives/${initiative.id}/documents/${idx}/download-url`, { headers: { Authorization: `Bearer ${token}` } });
      if (!urlRes.ok) throw new Error(`No se pudo generar el enlace (${urlRes.status})`);
      const { url } = await urlRes.json();
      await safeDownloadBlob(`${API}${url}`, doc.filename || "documento", token);
    } catch {
      toast.error("Error al descargar el documento");
    }
  };

  return (
    <Card data-testid="initiative-documents-card">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-base">Documentos</CardTitle>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-document-btn">
          {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
          Subir
        </Button>
        <input ref={fileRef} type="file" className="hidden" onChange={handleUpload} data-testid="upload-document-input" />
      </CardHeader>
      <CardContent>
        {docs.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="documents-empty">Sin documentos todavía.</p>
        ) : (
          <div className="space-y-2" data-testid="documents-list">
            {docs.map((d, idx) => (
              <div key={idx} className="flex items-center justify-between gap-2 text-sm" data-testid={`document-row-${idx}`}>
                <span className="flex items-center gap-1.5 truncate"><FileText className="w-3.5 h-3.5 flex-shrink-0 text-muted-foreground" />{d.filename}</span>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleDownload(d, idx)} data-testid={`download-document-${idx}`}>
                    <Download className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                  </button>
                  <button onClick={() => handleDelete(idx)} data-testid={`delete-document-${idx}`}>
                    <Trash2 className="w-3.5 h-3.5 text-muted-foreground hover:text-red-600" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
